import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LocationDetectionService } from '../services/locationDetectionService';

export type Region = 'EUR' | 'USA' | 'CHE';

interface RegionInfo {
  code: Region;
  name: string;
  currency: string;
  symbol: string;
}

export const SUPPORTED_REGIONS: Record<Region, RegionInfo> = {
  'EUR': { code: 'EUR', name: 'Europe', currency: 'EUR', symbol: '€' },
  'USA': { code: 'USA', name: 'United States', currency: 'USD', symbol: '$' },
  'CHE': { code: 'CHE', name: 'Switzerland', currency: 'CHF', symbol: 'CHF' }
};

interface RegionContextType {
  region: Region;
  setRegion: (region: Region) => void;
  regionInfo: RegionInfo;
  isRegionDetected: boolean;
  formatPrice: (price: number) => string;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

interface RegionProviderProps {
  children: ReactNode;
}

// Country code to region mapping
const COUNTRY_TO_REGION_MAP: Record<string, Region> = {
  // European countries -> EUR region
  'DE': 'EUR', 'AT': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 
  'NL': 'EUR', 'BE': 'EUR', 'PT': 'EUR', 'IE': 'EUR', 'LU': 'EUR',
  'FI': 'EUR', 'SK': 'EUR', 'SI': 'EUR', 'EE': 'EUR', 'LV': 'EUR',
  'LT': 'EUR', 'CY': 'EUR', 'MT': 'EUR', 'GR': 'EUR',
  
  // United States
  'US': 'USA',
  
  // Switzerland
  'CH': 'CHE',
  
  // Default to EUR for other countries
};

export function RegionProvider({ children }: RegionProviderProps) {
  const [region, setRegion] = useState<Region>('EUR');
  const [isRegionDetected, setIsRegionDetected] = useState<boolean>(false);

  // Get stored region from localStorage
  const getStoredRegion = (): Region | null => {
    try {
      const stored = localStorage.getItem('niveau-eleve-region');
      if (stored && Object.keys(SUPPORTED_REGIONS).includes(stored)) {
        return stored as Region;
      }
    } catch (error) {
      console.warn('Failed to read stored region:', error);
    }
    return null;
  };

  // Store user's manual region selection
  const storeRegion = (region: Region): void => {
    try {
      localStorage.setItem('niveau-eleve-region', region);
      localStorage.setItem('niveau-eleve-region-manual', 'true');
    } catch (error) {
      console.warn('Failed to store region:', error);
    }
  };

  // Check if user has manually selected a region
  const isManualRegionSelection = (): boolean => {
    try {
      return localStorage.getItem('niveau-eleve-region-manual') === 'true';
    } catch (error) {
      return false;
    }
  };

  // Map country code to region
  const mapCountryToRegion = (countryCode: string): Region => {
    return COUNTRY_TO_REGION_MAP[countryCode.toUpperCase()] || 'EUR';
  };

  // Auto-detect region on component mount
  useEffect(() => {
    const initializeRegion = async () => {
      // If user has manually selected a region before, use that
      const storedRegion = getStoredRegion();
      if (storedRegion && isManualRegionSelection()) {
        setRegion(storedRegion);
        setIsRegionDetected(true);
        return;
      }

      // Otherwise, auto-detect based on location
      try {
        const locationInfo = await LocationDetectionService.getLocationInfo();
        if (locationInfo.countryCode) {
          const detectedRegion = mapCountryToRegion(locationInfo.countryCode);
          setRegion(detectedRegion);
          setIsRegionDetected(true);
          console.log(`Detected country: ${locationInfo.countryCode}, setting region to: ${detectedRegion}`);
        } else {
          // Fallback to EUR
          setRegion('EUR');
          setIsRegionDetected(true);
          console.log('Could not detect country, defaulting to EUR region');
        }
      } catch (error) {
        console.warn('Region detection failed, using default EUR:', error);
        setRegion('EUR');
        setIsRegionDetected(true);
      }
    };

    initializeRegion();
  }, []);

  // Enhanced setRegion function that stores manual selections
  const handleSetRegion = (newRegion: Region): void => {
    setRegion(newRegion);
    storeRegion(newRegion);
  };

  // Format price based on region
  const formatPrice = (price: number): string => {
    const regionInfo = SUPPORTED_REGIONS[region];
    
    try {
      // Use Intl.NumberFormat for proper currency formatting
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: regionInfo.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      
      return formatter.format(price);
    } catch (error) {
      // Fallback to simple formatting if Intl.NumberFormat fails
      return `${regionInfo.symbol}${price.toLocaleString()}`;
    }
  };

  const regionInfo = SUPPORTED_REGIONS[region];

  return (
    <RegionContext.Provider value={{ 
      region, 
      setRegion: handleSetRegion, 
      regionInfo,
      isRegionDetected,
      formatPrice
    }}>
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  const context = useContext(RegionContext);
  if (context === undefined) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
}